<template>
  <div class="analysis-page">
    <!-- 头部：标题 + 日期范围筛选 + 刷新 -->
    <header class="analysis-heading">
      <div>
        <h1>数据分析</h1>
        <p>围绕租户规模、运营模式、资源使用和有效期进行集中分析。</p>
      </div>
      <div class="analysis-filters">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          clearable
          style="width: 236px">
        </el-date-picker>
        <el-button size="small" icon="el-icon-refresh" :loading="refreshing" @click="refreshData">刷新数据</el-button>
      </div>
    </header>

    <!-- 顶部统计 banner -->
    <section class="analysis-stat-grid" v-loading="bannerLoading">
      <div class="analysis-stat-card">
        <span>租户总量</span>
        <div class="analysis-stat-card__main">
          <strong>{{ banner.tenantTotal.total }}</strong>
          <div class="analysis-stat-card__breakdown">
            <span class="is-group"><b>{{ banner.tenantTotal.groupCount }}</b> 集团</span>
            <span class="is-general"><b>{{ banner.tenantTotal.generalCount }}</b> 一般</span>
            <span class="is-localized"><b>{{ banner.tenantTotal.localCount }}</b> 本地化</span>
          </div>
        </div>
        <small>当前筛选范围内</small>
      </div>
      <div class="analysis-stat-card">
        <span>资源消耗</span>
        <div class="analysis-stat-card__main">
          <div class="analysis-stat-card__segments">
            <div class="analysis-stat-card__segment">
              <b>{{ banner.resourceConsume.aiUsed | formatNum }}</b>
              <span>AI 点数</span>
            </div>
            <div class="analysis-stat-card__segment">
              <b>{{ banner.resourceConsume.smsUsed | formatNum }}</b>
              <span>短信点数</span>
            </div>
          </div>
        </div>
        <small>当前筛选范围累计消耗</small>
      </div>
      <div class="analysis-stat-card">
        <span>二次续费客户</span>
        <div class="analysis-stat-card__main">
          <strong>{{ banner.renewTwiceTotal.total }}</strong>
          <div class="analysis-stat-card__breakdown">
            <span class="is-group"><b>{{ banner.renewTwiceTotal.groupCount }}</b> 集团</span>
            <span class="is-general"><b>{{ banner.renewTwiceTotal.generalCount }}</b> 一般</span>
            <span class="is-localized"><b>{{ banner.renewTwiceTotal.localCount }}</b> 本地化</span>
          </div>
        </div>
        <small>已完成二次续费</small>
      </div>
      <div class="analysis-stat-card">
        <span>总使用人数</span>
        <div class="analysis-stat-card__main">
          <strong>{{ banner.userTotal | formatNum }}</strong>
        </div>
        <small>当前筛选范围累计使用</small>
      </div>
    </section>

    <!-- 近期到期单位 -->
    <section class="analysis-card expiry-list-card">
      <div class="analysis-card__heading">
        <div>
          <strong>近期到期单位</strong>
          <small>优先展示 90 天内需要关注的单位</small>
        </div>
        <el-tag v-if="expireUnits.total" type="warning" effect="light">{{ expireUnits.total }} 家待关注</el-tag>
      </div>
      <div v-if="expireUnits.list.length" class="expiry-table" v-loading="expireLoading">
        <div class="expiry-table__row expiry-table__head">
          <span>单位名称</span>
          <span>有效期</span>
          <span>剩余天数</span>
        </div>
        <div v-for="row in expireUnits.list" :key="row.tenantId" class="expiry-table__row">
          <div class="expiry-unit-name">
            <strong>{{ row.name }}</strong>
            <el-tag size="small" :type="row.tenantType === 2 ? '' : 'success'">{{ row.tenantTypeLabel }}</el-tag>
          </div>
          <span>{{ row.expDate }}</span>
          <span>
            <el-tag size="small" :type="row.remainDays <= 30 ? 'danger' : 'warning'">{{ row.remainDays }} 天</el-tag>
          </span>
        </div>
      </div>
      <el-empty v-else description="暂无 90 天内到期单位" :image-size="54"></el-empty>
    </section>

    <!-- 租户类型 + 有效期年度分布 -->
    <section class="analysis-grid">
      <div class="analysis-card" v-loading="typeLoading">
        <div class="analysis-card__heading">
          <div>
            <strong>租户类型</strong>
            <small>集团租户与一般租户的数量比例</small>
          </div>
        </div>
        <ve-ring
          v-if="tenantTypeRows.length"
          :data="{ columns: ['类型', '数量'], rows: tenantTypeRows }"
          :settings="ringSettings"
          :colors="ringColors"
          :extend="ringExtend"
          height="260px">
        </ve-ring>
        <el-empty v-else description="暂无数据" :image-size="54"></el-empty>
      </div>
      <div class="analysis-card" v-loading="yearLoading">
        <div class="analysis-card__heading">
          <div>
            <strong>有效期年度分布</strong>
            <small>按单位授权有效年份进行分布</small>
          </div>
        </div>
        <ve-histogram
          v-if="expireYearRows.length"
          :data="{ columns: ['年份', '单位数量'], rows: expireYearRows }"
          :colors="yearBarColors"
          :extend="yearBarExtend"
          height="260px">
        </ve-histogram>
        <el-empty v-else description="暂无数据" :image-size="54"></el-empty>
      </div>
    </section>

    <!-- 资源使用分析 + 资源使用 Top5 -->
    <section class="analysis-grid analysis-grid--bottom">
      <div class="analysis-card" v-loading="resourceUseLoading">
        <div class="analysis-card__heading">
          <div>
            <strong>资源使用分析</strong>
            <small>账户数、AI 识别和短信额度的总使用量</small>
          </div>
        </div>
        <ve-bar
          v-if="resourceUseRows.length"
          :data="{ columns: ['类型', '使用量'], rows: resourceUseRows }"
          :colors="resourceBarColors"
          :extend="resourceBarExtend"
          height="300px">
        </ve-bar>
        <el-empty v-else description="暂无数据" :image-size="54"></el-empty>
      </div>
      <div class="analysis-card analysis-ranking-card" v-loading="top5Loading">
        <div class="analysis-card__heading">
          <div>
            <strong>资源使用 Top 5</strong>
            <small>分别展示 AI、短信和人员数 Top 5</small>
          </div>
        </div>
        <div class="ranking-list">
          <div v-for="group in top5Groups" :key="group.key" class="ranking-group">
            <strong class="ranking-group__title">{{ group.label }} Top 5</strong>
            <template v-if="group.items.length">
              <div v-for="(item, index) in group.items" :key="group.key + item.tenantId" class="ranking-item">
                <span class="ranking-item__index" :class="{ 'is-top': index < 3 }">{{ index + 1 }}</span>
                <span class="ranking-item__name" :title="item.name">{{ item.name }}</span>
                <div class="ranking-item__progress">
                  <i :style="{ width: top5Width(item.value, group.items) }"></i>
                </div>
                <strong class="ranking-item__value">{{ item.value | formatNum }}</strong>
              </div>
            </template>
            <el-empty v-else description="暂无数据" :image-size="40"></el-empty>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import VeRing from 'v-charts/lib/ring.common';
import VeHistogram from 'v-charts/lib/histogram.common';
import VeBar from 'v-charts/lib/bar.common';

export default {
  name: 'DataAnalysis', // 数据分析
  components: { VeRing, VeHistogram, VeBar },
  props: {},
  data () {
    return {
      // 日期筛选范围，默认当前年份
      dateRange: [],
      refreshing: false,
      // 顶部统计 banner
      bannerLoading: false,
      banner: {
        tenantTotal: { total: 0, groupCount: 0, generalCount: 0, localCount: 0 },
        resourceConsume: { aiUsed: 0, smsUsed: 0 },
        renewTwiceTotal: { total: 0, groupCount: 0, generalCount: 0, localCount: 0 },
        userTotal: 0
      },
      // 近期到期单位
      expireLoading: false,
      expireUnits: { total: 0, list: [] },
      // 租户类型占比
      typeLoading: false,
      tenantTypeRows: [],
      ringColors: ['#3478ee', '#67c23a'],
      ringSettings: {
        radius: ['48%', '72%'],
        label: {
          show: true,
          formatter: '{b}\n{c} 家',
          color: '#606266',
          fontSize: 12
        }
      },
      ringExtend: {
        series: { center: ['50%', '45%'] },
        legend: {
          bottom: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: '#606266', fontSize: 12 }
        }
      },
      // 有效期年度分布
      yearLoading: false,
      expireYearRows: [],
      yearBarColors: ['#e6a23c'],
      yearBarExtend: {
        legend: { show: false },
        grid: { left: 56, right: 24, top: 18, bottom: 30 },
        xAxis: {
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: { color: '#606266' }
        },
        yAxis: {
          minInterval: 1,
          splitLine: { lineStyle: { color: '#f0f2f5' } },
          axisLabel: { color: '#909399' }
        },
        series: {
          barWidth: 30,
          itemStyle: { borderRadius: [4, 4, 0, 0] },
          label: { show: true, position: 'top', color: '#606266' }
        }
      },
      // 资源使用分析
      resourceUseLoading: false,
      resourceUseRows: [],
      resourceBarColors: ['#3478ee'],
      resourceBarExtend: {
        legend: { show: false },
        grid: { left: 76, right: 32, top: 20, bottom: 22 },
        xAxis: {
          axisLabel: { color: '#909399' },
          splitLine: { lineStyle: { color: '#f0f2f5' } }
        },
        yAxis: {
          axisLabel: { color: '#606266' }
        },
        series: {
          barWidth: 20,
          itemStyle: { borderRadius: [0, 4, 4, 0] },
          label: { show: true, position: 'right', color: '#606266' }
        }
      },
      // 资源使用 Top5
      top5Loading: false,
      top5: { aiTop5: [], smsTop5: [], accountTop5: [] }
    }
  },
  filters: {
    formatNum (val) {
      return Number(val || 0).toLocaleString()
    }
  },
  computed: {
    queryParams () {
      const params = {}
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0]
        params.endDate = this.dateRange[1]
      }
      return params
    },
    top5Groups () {
      return [
        { key: 'ai', label: 'AI 使用量', items: this.top5.aiTop5 },
        { key: 'sms', label: '短信使用量', items: this.top5.smsTop5 },
        { key: 'account', label: '使用人员数', items: this.top5.accountTop5 }
      ]
    }
  },
  watch: {
    dateRange () {
      this.refreshData()
    }
  },
  methods: {
    /**
     * 顶部统计 banner
     */
    getBanner () {
      this.bannerLoading = true
      this.$api.tenantAnalysis.getBanner(this.queryParams).then(res => {
        this.bannerLoading = false
        this.banner.tenantTotal = res.tenantTotal || this.banner.tenantTotal
        this.banner.resourceConsume = res.resourceConsume || this.banner.resourceConsume
        this.banner.renewTwiceTotal = res.renewTwiceTotal || this.banner.renewTwiceTotal
        this.banner.userTotal = res.userTotal || 0
      }).catch(() => {
        this.bannerLoading = false
      })
    },
    /**
     * 近期到期单位（90 天内）
     */
    getExpireUnits () {
      this.expireLoading = true
      this.$api.tenantAnalysis.getExpireUnits(this.queryParams).then(res => {
        this.expireLoading = false
        this.expireUnits.total = res.total || 0
        this.expireUnits.list = res.list || []
      }).catch(() => {
        this.expireLoading = false
      })
    },
    /**
     * 租户类型占比
     */
    getTenantType () {
      this.typeLoading = true
      this.$api.tenantAnalysis.getTenantType(this.queryParams).then(res => {
        this.typeLoading = false
        const rows = []
        if (res.groupCount) rows.push({ 类型: '集团租户', 数量: res.groupCount })
        if (res.generalCount) rows.push({ 类型: '一般租户', 数量: res.generalCount })
        this.tenantTypeRows = rows
      }).catch(() => {
        this.typeLoading = false
      })
    },
    /**
     * 有效期年度分布
     */
    getExpireYearDist () {
      this.yearLoading = true
      this.$api.tenantAnalysis.getExpireYearDist(this.queryParams).then(res => {
        this.yearLoading = false
        this.expireYearRows = (res || []).map(item => {
          return { 年份: String(item.year), 单位数量: item.count }
        })
      }).catch(() => {
        this.yearLoading = false
      })
    },
    /**
     * 资源使用分析
     */
    getResourceUse () {
      this.resourceUseLoading = true
      this.$api.tenantAnalysis.getResourceUse(this.queryParams).then(res => {
        this.resourceUseLoading = false
        this.resourceUseRows = [
          { 类型: '短信额度', 使用量: res.smsUsed || 0 },
          { 类型: 'AI 识别', 使用量: res.aiUsed || 0 },
          { 类型: '账户数', 使用量: res.accountSize || 0 }
        ]
      }).catch(() => {
        this.resourceUseLoading = false
      })
    },
    /**
     * 资源使用 Top5
     */
    getResourceTop5 () {
      this.top5Loading = true
      this.$api.tenantAnalysis.getResourceTop5(this.queryParams).then(res => {
        this.top5Loading = false
        this.top5.aiTop5 = res.aiTop5 || []
        this.top5.smsTop5 = res.smsTop5 || []
        this.top5.accountTop5 = res.accountTop5 || []
      }).catch(() => {
        this.top5Loading = false
      })
    },
    /**
     * Top5 进度条宽度（按当前列最大值归一）
     */
    top5Width (value, list) {
      const max = Math.max(...list.map(item => item.value || 0), 0)
      if (!max) return '0%'
      return `${Math.max(Math.round((value || 0) / max * 100), 4)}%`
    },
    /**
     * 刷新全部数据
     */
    refreshData () {
      this.refreshing = true
      this.getBanner()
      this.getExpireUnits()
      this.getTenantType()
      this.getExpireYearDist()
      this.getResourceUse()
      this.getResourceTop5()
      setTimeout(() => {
        this.refreshing = false
      }, 500)
    }
  },
  beforeCreate () {
  },
  created () {
    // 默认筛选当前年份
    const year = new Date().getFullYear()
    this.dateRange = [`${year}-01-01`, `${year}-12-31`]
    this.refreshData()
  },
  beforeMount () {
  },
  mounted () {
  },
  beforeUpdate () {
  },
  updated () {
  },
  beforeDestroy () {
  },
  destroyed () {
  }
}
</script>

<style lang="scss" scoped>
.analysis-page {
  min-height: calc(100vh - 104px);
  margin: -20px;
  padding: 24px;
  background: #f5f5f5;
  color: #303133;
}
.analysis-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
  p {
    margin: 8px 0 0;
    color: #909399;
    font-size: 13px;
  }
}
.analysis-filters {
  display: flex;
  align-items: center;
  gap: 8px;
}
/* 统计卡片 */
.analysis-stat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.analysis-stat-card {
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  min-height: 104px;
  padding: 18px 20px;
  & > span {
    display: block;
    color: #909399;
    font-size: 12px;
  }
  & > small {
    display: block;
    color: #909399;
    font-size: 12px;
  }
  strong {
    display: block;
    color: #303133;
    font-size: 26px;
    font-weight: 600;
  }
  .analysis-stat-card__main {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin: 8px 0 4px;
  }
  .analysis-stat-card__segments {
    display: flex;
    align-items: stretch;
    gap: 18px;
  }
  .analysis-stat-card__segment {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 72px;
    & + .analysis-stat-card__segment {
      padding-left: 18px;
      border-left: 1px solid #ebeef5;
    }
    b {
      color: #303133;
      font-size: 22px;
      line-height: 1;
      font-weight: 600;
    }
    span {
      color: #909399;
      font-size: 11px;
    }
  }
  .analysis-stat-card__breakdown {
    display: flex;
    align-items: center;
    gap: 6px;
    span {
      display: inline-flex;
      align-items: baseline;
      gap: 2px;
      padding: 3px 6px;
      border-radius: 3px;
      font-size: 11px;
    }
    b {
      font-size: 14px;
      font-weight: 600;
    }
    .is-group {
      color: #3478ee;
      background: #eef4ff;
    }
    .is-general {
      color: #67c23a;
      background: #f0f9eb;
    }
    .is-localized {
      color: #909399;
      background: #f4f4f5;
    }
  }
}
/* 通用卡片 */
.analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.analysis-card {
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 18px 20px;
  .analysis-card__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    margin-bottom: 10px;
    strong {
      display: block;
      font-size: 15px;
    }
    small {
      display: block;
      margin-top: 5px;
      color: #909399;
      font-size: 12px;
    }
  }
}
.analysis-stat-card,
.analysis-card {
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    border-color: #d6e4f5;
    box-shadow: 0 4px 14px rgba(31, 45, 61, 0.1);
  }
}
/* 近期到期单位 */
.expiry-list-card {
  margin-bottom: 16px;
}
.expiry-table {
  .expiry-table__row {
    display: grid;
    grid-template-columns: minmax(220px, 1.5fr) 1fr 100px;
    align-items: center;
    min-height: 44px;
    border-bottom: 1px solid #f2f4f7;
    color: #606266;
    font-size: 13px;
    &:last-child {
      border-bottom: 0;
    }
    &.expiry-table__head {
      min-height: 36px;
      color: #909399;
      font-size: 12px;
    }
    strong {
      color: #303133;
      font-weight: 500;
    }
  }
  .expiry-unit-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
}
/* Top5 排行 */
.analysis-ranking-card {
  min-height: 420px;
}
.ranking-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-top: 18px;
}
.ranking-group {
  min-width: 0;
  .ranking-group__title {
    display: block;
    padding-bottom: 8px;
    color: #303133;
    font-size: 13px;
  }
}
.ranking-item {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 42px;
  padding: 8px 0;
  border-bottom: 1px solid #f2f4f7;
  font-size: 12px;
  &:last-child {
    border-bottom: 0;
  }
  .ranking-item__index {
    display: inline-flex;
    flex: 0 0 20px;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: #909399;
    background: #f4f4f5;
    border-radius: 50%;
    &.is-top {
      color: #ffffff;
      background: #3478ee;
    }
  }
  .ranking-item__name {
    flex: 0 0 92px;
    overflow: hidden;
    color: #606266;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ranking-item__progress {
    flex: 1;
    height: 6px;
    overflow: hidden;
    background: #edf2fc;
    border-radius: 4px;
    i {
      display: block;
      height: 100%;
      background: #67c23a;
      border-radius: 4px;
      transition: width 0.4s;
    }
  }
  .ranking-item__value {
    flex: 0 0 34px;
    color: #606266;
    font-size: 11px;
    font-weight: 500;
    text-align: right;
  }
}
@media (max-width: 900px) {
  .analysis-page {
    padding: 16px;
  }
  .analysis-stat-grid,
  .analysis-grid {
    grid-template-columns: 1fr 1fr;
  }
  .analysis-grid--bottom {
    grid-template-columns: 1fr;
  }
}
@media (prefers-reduced-motion: reduce) {
  .analysis-stat-card,
  .analysis-card {
    transition: none;
  }
}
</style>
